import { format, formatTime, plural } from '@rental/i18n'
import { Button, Checkbox, cn, Field, Notice } from '@rental/ui'
import { Inbox, RotateCcw, Trash2 } from 'lucide-react'
import { type ReactNode, type RefObject, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Dialog } from '../components/Dialog'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { problemText } from '../problems'
import { useCapture } from './capture-context'
import { usePhotoUrl, useQueueSummary } from './hooks'
import type { CaptureItem, CaptureState } from './item'
import { isStale, verdictOf, waitsForDamage } from './queue'

type Messages = ReturnType<typeof useLocale>['messages']

const TONE: Readonly<Record<CaptureState, ChipTone>> = {
  queued: 'warning',
  sending: 'info',
  sent: 'success',
  refused: 'danger',
}

/** Where an item stands, in a colour, an icon and a word (DESIGN.md). */
export function CaptureChip({ item }: { item: CaptureItem }) {
  const { messages: m } = useLocale()
  const words = item.kind === 'photo' ? m.capture.photoStates : m.capture.damageStates
  return <StatusChip tone={TONE[item.state]}>{words[item.state]}</StatusChip>
}

/** Riprova only where trying again can work: never for a refusal that would repeat. */
export function canRetry(item: CaptureItem): boolean {
  if (item.state !== 'refused' || !item.problem) return false
  const verdict = verdictOf(item.problem)
  return verdict.kind !== 'refused' || verdict.retryable
}

/** Scarta only by the person who took it, and never while it is on its way. */
export function canDiscard(item: CaptureItem, userId: string | null): boolean {
  return item.capturedBy.userId === userId && (item.state === 'queued' || item.state === 'refused')
}

/** Why an item waits or was refused, in words; null when there is nothing to add. */
export function useWhy(): (item: CaptureItem) => string | null {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const { items } = useCapture()
  const who = access.state === 'unlocked' ? access.session.user.id : null
  const byId = useMemo(() => new Map((items ?? []).map((item) => [item.id, item])), [items])
  return (item) => {
    if (item.state === 'refused' && item.problem) return problemText(item.problem, locale, m)
    if (item.state !== 'queued') return null
    if (item.capturedBy.userId !== who) {
      return format(m.capture.waitsFor, { name: item.capturedBy.name })
    }
    if (waitsForDamage(item, byId)) return m.capture.waitsForDamage
    if (item.problem?.kind === 'network') return m.capture.noConnection
    if (item.problem) return m.capture.serverBusy
    return null
  }
}

/** Asks before items leave the iPad unsent: they cannot be recovered. */
export function useDiscard(): { ask: (ids: readonly string[]) => void; dialog: ReactNode } {
  const { locale, messages: m } = useLocale()
  const { discard } = useCapture()
  const dialog = useRef<HTMLDialogElement>(null)
  const [ids, setIds] = useState<readonly string[]>([])
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (ids.length > 0 && !dialog.current?.open) dialog.current?.showModal()
  }, [ids])
  return {
    ask: (chosen) => setIds(chosen),
    dialog: (
      <ConfirmDialog
        dialogRef={dialog}
        title={plural(locale, m.capture.discardTitle, ids.length)}
        confirmLabel={m.capture.discard}
        cancelLabel={m.capture.keep}
        busy={busy}
        onClose={() => setIds([])}
        onConfirm={() => {
          setBusy(true)
          void discard(ids).finally(() => {
            setBusy(false)
            dialog.current?.close()
          })
        }}
      >
        <p>{m.capture.discardBody}</p>
      </ConfirmDialog>
    ),
  }
}

/** A photo kept on the iPad, shown from its own memory. */
export function LocalImage({ item, alt }: { item: CaptureItem; alt: string }) {
  const url = usePhotoUrl(item.kind === 'photo' ? item : null)
  const frame = 'aspect-4/3 w-full rounded-sm border border-rule bg-bg object-cover'
  return url ? <img src={url} alt={alt} className={frame} /> : <div className={frame} />
}

/** What the item is, in a line: the zone, and for a damage its words. */
export function itemTitle(item: CaptureItem, m: Messages): string {
  const zone = m.inspection.zones[item.zone]
  return item.kind === 'photo'
    ? format(m.capture.photoOf, { zone })
    : format(m.capture.damageOf, { zone, description: item.description })
}

/**
 * One item with its state, why, and what its person can do: Riprova where it
 * can work, Scarta (asked first). Thumbnails only for the person unlocked:
 * another person's photos are theirs to look at.
 */
export function ItemRow({
  item,
  onDiscard,
  showWhere = false,
}: {
  item: CaptureItem
  onDiscard: (ids: readonly string[]) => void
  showWhere?: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const { retry } = useCapture()
  const why = useWhy()(item)
  const who = access.state === 'unlocked' ? access.session.user.id : null
  const mine = item.capturedBy.userId === who
  const reason = why ? <p className="text-sm">{why}</p> : null
  // Riprova and Scarta keep their one word, and say which item they act on.
  const titleId = useId()
  return (
    <li className="flex flex-wrap items-start gap-3 rounded-sm bg-surface-well p-4">
      {item.kind === 'photo' && mine ? (
        <div className="w-1/4 shrink-0">
          <LocalImage item={item} alt={itemTitle(item, m)} />
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="flex flex-wrap items-center gap-2">
          <span id={titleId} className="font-medium">
            {itemTitle(item, m)}
          </span>
          <CaptureChip item={item} />
        </p>
        {showWhere ? (
          <p className="text-sm text-text-muted">
            {format(m.capture.where, {
              plate: item.plate,
              code: item.bookingCode,
              stage: item.stage === 'checkout' ? m.inspection.atCheckout : m.inspection.atReturn,
            })}
          </p>
        ) : null}
        <p className="text-sm text-text-muted">
          {format(m.capture.takenBy, {
            name: item.capturedBy.name,
            time: formatTime(locale, new Date(item.capturedAt).toISOString()),
          })}
        </p>
        {reason}
      </div>
      {mine && (canRetry(item) || canDiscard(item, who)) ? (
        <div className="flex flex-wrap gap-2">
          {canRetry(item) ? (
            <Button
              variant="outline"
              className="rounded-sm"
              aria-describedby={titleId}
              onClick={() => void retry(item.id)}
            >
              <RotateCcw aria-hidden="true" className="size-4" />
              {m.capture.retry}
            </Button>
          ) : null}
          {canDiscard(item, who) ? (
            <Button
              variant="outline"
              className="rounded-sm"
              aria-describedby={titleId}
              onClick={() => onDiscard([item.id])}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              {m.capture.discard}
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

/**
 * S4: a hand-over or a return waits for its own inspection on this iPad.
 * It says what is left and whose it is; the person who took the items may
 * discard them, and the Owner may go ahead without them, with a reason the
 * server records. Items of another vehicle considered at a hand-over are
 * listed, and hold up nothing.
 */
export function UnsentInspectionNotice({
  stage,
  unsent,
  elsewhere,
  known,
  canOverride,
  leave,
  onLeave,
  reason,
  onReason,
}: {
  stage: 'checkout' | 'return'
  unsent: readonly CaptureItem[]
  elsewhere: readonly CaptureItem[]
  known: boolean
  canOverride: boolean
  leave: boolean
  onLeave: (leave: boolean) => void
  reason: string
  onReason: (reason: string) => void
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const { ask, dialog } = useDiscard()
  const titleId = useId()
  const who = access.state === 'unlocked' ? access.session.user.id : null
  if (!known) return <p className="text-text-muted">{m.capture.reading}</p>
  const others = new Map<string, { name: string; count: number }>()
  for (const item of unsent) {
    if (item.capturedBy.userId === who) continue
    const person = others.get(item.capturedBy.userId) ?? { name: item.capturedBy.name, count: 0 }
    person.count++
    others.set(item.capturedBy.userId, person)
  }
  const mine = unsent.filter((item) => canDiscard(item, who)).map((item) => item.id)
  return (
    <>
      {unsent.length > 0 ? (
        // A group named by its count, holding the items and what can be done with them. Its
        // words alone are the polite status: a reason typed under them is never read back.
        <fieldset aria-labelledby={titleId} className="min-w-0">
          <Notice tone="warning">
            <div role="status" className="flex flex-col gap-1">
              <p id={titleId} className="font-bold text-warning">
                {plural(locale, m.capture.unsent, unsent.length)}
              </p>
              <p>{stage === 'checkout' ? m.capture.waitCheckout : m.capture.waitReturn}</p>
              {[...others.values()].map((person) => (
                <p key={person.name}>
                  {plural(locale, m.capture.theirs, person.count, { name: person.name })}
                </p>
              ))}
            </div>
            <ul className="flex flex-col gap-2 pt-2">
              {unsent.map((item) => (
                <ItemRow key={item.id} item={item} onDiscard={ask} />
              ))}
            </ul>
            {mine.length > 1 ? (
              <div className="pt-2">
                <Button variant="outline" className="rounded-sm" onClick={() => ask(mine)}>
                  <Trash2 aria-hidden="true" className="size-4" />
                  {m.capture.discardMine}
                </Button>
              </div>
            ) : null}
            {canOverride ? (
              <div className="flex flex-col gap-3 pt-2">
                <Checkbox
                  label={m.capture.ownerProceed}
                  checked={leave}
                  onChange={(event) => onLeave(event.target.checked)}
                />
                {leave ? (
                  <Field label={m.capture.ownerReason} hint={m.capture.ownerReasonHint} required>
                    {(control) => (
                      <StaffInput
                        {...control}
                        maxLength={500}
                        value={reason}
                        onChange={(event) => onReason(event.target.value)}
                      />
                    )}
                  </Field>
                ) : null}
              </div>
            ) : null}
          </Notice>
        </fieldset>
      ) : null}
      {elsewhere.length > 0 ? (
        <Notice tone="info">
          <p>{plural(locale, m.capture.elsewhere, elsewhere.length)}</p>
          <ul className="flex flex-col gap-2 pt-2">
            {elsewhere.map((item) => (
              <ItemRow key={item.id} item={item} onDiscard={ask} showWhere />
            ))}
          </ul>
        </Notice>
      ) : null}
      {dialog}
    </>
  )
}

/**
 * The list of every item kept on this iPad, in a dialog. Its contents exist
 * only while it is open: no thumbnails are made for a list nobody sees.
 */
function useQueueDialog(): { open: boolean; show: () => void; element: ReactNode } {
  const dialog = useRef<HTMLDialogElement>(null)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (open && !dialog.current?.open) dialog.current?.showModal()
  }, [open])
  return {
    open,
    show: () => setOpen(true),
    element: <QueueDialog dialogRef={dialog} open={open} onClose={() => setOpen(false)} />,
  }
}

/**
 * The shell's line for what this iPad still holds: shown to those who
 * inspect (rentals.manage), opening the list of every item kept here.
 */
export function QueueIndicator() {
  const { locale, messages: m } = useLocale()
  const summary = useQueueSummary()
  const list = useQueueDialog()
  const hintId = useId()
  // The list stays open when its last item goes: it says so, and closes when asked.
  if (summary.unsent === 0 && !list.open) return null
  return (
    <>
      {summary.unsent > 0 ? (
        // Named by what it shows (WCAG 2.5.3), a refusal in words as well as in red.
        <Button
          variant="outline"
          className={cn('rounded-sm', summary.refused > 0 ? 'border-danger' : '')}
          aria-describedby={hintId}
          onClick={list.show}
        >
          <Inbox aria-hidden="true" className="size-4" />
          {format(m.capture.indicator, { count: summary.unsent })}
          {summary.refused > 0
            ? `, ${plural(locale, m.capture.indicatorRefused, summary.refused)}`
            : null}
          <span id={hintId} hidden>
            {m.capture.indicatorHint}
          </span>
        </Button>
      ) : null}
      {list.element}
    </>
  )
}

/** Every unsent item kept on this iPad, with what can be done about each. */
function QueueDialog({
  dialogRef,
  open,
  onClose,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  open: boolean
  onClose: () => void
}) {
  const { messages: m } = useLocale()
  const { items, durable } = useCapture()
  const { ask, dialog } = useDiscard()
  const unsent = (items ?? [])
    .filter((item) => item.state !== 'sent')
    .sort((a, b) => a.capturedAt - b.capturedAt)
  return (
    <>
      <Dialog
        dialogRef={dialogRef}
        title={m.capture.listTitle}
        onClose={onClose}
        className="max-w-xl"
      >
        {open ? (
          <div className="flex flex-col gap-4">
            {durable ? null : (
              <Notice tone="warning">
                <p>{m.capture.notDurable}</p>
              </Notice>
            )}
            {unsent.length === 0 ? <p>{m.capture.listEmpty}</p> : null}
            <ul className="flex flex-col gap-2">
              {unsent.map((item) => (
                <ItemRow key={item.id} item={item} onDiscard={ask} showWhere />
              ))}
            </ul>
            <div className="flex justify-end">
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => dialogRef.current?.close()}
              >
                {m.capture.close}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
      {dialog}
    </>
  )
}

/** ARCHITECTURE: anything unsent for over 15 minutes is said on every screen. */
export function StaleBanner() {
  const { messages: m } = useLocale()
  const summary = useQueueSummary()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])
  const list = useQueueDialog()
  if (!isStale(summary, now) && !list.open) return null
  if (!isStale(summary, now)) return list.element
  return (
    <>
      <Notice tone="warning">
        <p>
          {m.capture.stale}{' '}
          <Button variant="link" onClick={list.show}>
            {m.capture.staleLink}
          </Button>
        </p>
      </Notice>
      {list.element}
    </>
  )
}

/** The lock and boot screens: what waits on this iPad for an unlock. */
export function KeptOnIpad() {
  const { locale, messages: m } = useLocale()
  const summary = useQueueSummary()
  if (summary.unsent === 0) return null
  return (
    <Notice tone="info">
      <p>{plural(locale, m.capture.kept, summary.unsent)}</p>
    </Notice>
  )
}
